import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { api } from 'constants/constant';
import { getHeaders } from 'auth/auth';

/**
 * Branch management API slice
 * Provides tenant-scoped CRUD operations for organization branches
 */
export const branchManagementAPI = createApi({
  reducerPath: 'branchManagement',
  tagTypes: ['Branches', 'Branch'],
  baseQuery: fetchBaseQuery({
    baseUrl: `${api}/b2b/organizations`,
    credentials: 'include',
    prepareHeaders: (headers) => {
      const baseHeaders = getHeaders();
      Object.entries(baseHeaders).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          headers.set(key, value);
        }
      });
      return headers;
    },
    }),
  endpoints: (builder) => ({
    /**
     * List branches for the current tenant
     */
    listBranches: builder.query({
      query: ({ includeInactive = false } = {}) => ({
        url: `/branches` + (includeInactive ? '?include_inactive=true' : ''),
        method: 'GET',
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map((branch) => ({ type: 'Branch', id: branch.id })),
              { type: 'Branches', id: 'LIST' },
            ]
          : [{ type: 'Branches', id: 'LIST' }],
    }),

    /**
     * Retrieve a single branch by id
     */
    getBranch: builder.query({
      query: (branchId) => ({
        url: `/branches/${branchId}`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: 'Branch', id }],
    }),

    /**
     * Create a new branch for the tenant
     */
    createBranch: builder.mutation({
      query: (payload) => ({
        url: '/branches',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [{ type: 'Branches', id: 'LIST' }],
  }),

    /**
     * Update branch details or status
     */
    updateBranch: builder.mutation({
      query: ({ branchId, ...changes }) => ({
        url: `/branches/${branchId}`,
        method: 'PUT',
        body: changes,
      }),
      invalidatesTags: (result, error, { branchId }) => [
        { type: 'Branch', id: branchId },
        { type: 'Branches', id: 'LIST' },
      ],
    }),

    /**
     * Assign an NMLS-verified manager to a branch
     */
    assignBranchManager: builder.mutation({
      query: ({ branchId, managerUserId, notes }) => ({
        url: `/branches/${branchId}/manager`,
        method: 'POST',
        body: {
          manager_user_id: managerUserId,
          ...(notes ? { notes } : {}),
        },
      }),
      invalidatesTags: (result, error, { branchId }) => [
        { type: 'Branch', id: branchId },
        { type: 'Branches', id: 'LIST' },
      ],
    }),

    /**
     * Remove the current manager from a branch
     */
    unassignBranchManager: builder.mutation({
      query: ({ branchId }) => ({
        url: `/branches/${branchId}/manager`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { branchId }) => [
        { type: 'Branch', id: branchId },
        { type: 'Branches', id: 'LIST' },
      ],
    }),

    /**
     * Retrieve branch manager change history
     */
    getBranchManagerHistory: builder.query({
      query: ({ branchId, limit = 50 }) => ({
        url: `/branches/${branchId}/history?limit=${limit}`,
        method: 'GET',
      }),
      providesTags: (result, error, { branchId }) => [
        { type: 'Branch', id: branchId },
      ],
    }),
  }),
});

export const {
  useListBranchesQuery,
  useGetBranchQuery,
  useCreateBranchMutation,
  useUpdateBranchMutation,
  useAssignBranchManagerMutation,
  useUnassignBranchManagerMutation,
  useGetBranchManagerHistoryQuery,
} = branchManagementAPI;
